import React, { useState } from 'react';
import { DollarSign, Download, Lock, ListChecks, Wallet, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';
import { PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../utils/bookingSource';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const PERIOD_TABS = [
 { value: 'week', label: 'This week' },
 { value: 'month', label: 'This month' },
 { value: 'quarter', label: 'This quarter' },
 { value: 'year', label: 'This year' },
];

const VendorEarnings = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendorEarnings', period],
    queryFn: async () => {
      const response = await api.get(`/vendor/analytics/earnings?period=${period}`);
      return response.data;
 },
  });

  const earnings = data?.data || {};

  return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Earnings"
 subtitle="Track your platform revenue and private offline payments."
 actions={
 <button
 type="button"
 className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors text-sm font-medium"
 >
              <Download className="w-4 h-4" />
 Export
 </button>
 }
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.earnings.title}
 steps={VENDOR_PAGE_GUIDES.earnings.steps}
 icon={ListChecks}
 />

 <div className="product-panel overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Earnings period"
 >
 {PERIOD_TABS.map((tab) => {
 const isActive = period === tab.value;
 return (
 <button
 key={tab.value}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setPeriod(tab.value)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
            </button>
 );
 })}
          </div>
        </div>

 <div className="p-4 sm:p-5 space-y-5">
        {isLoading ? (
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 animate-pulse">
            {[1, 2, 3].map((i) => (
 <div
 key={i}
 className="rounded-xl border border-border bg-surface-muted p-5"
 >
 <div className="h-3 w-28 rounded bg-gray-200 mb-4" />
 <div className="h-8 w-24 rounded bg-gray-200" />
              </div>
            ))}
          </div>
        ) : error ? (
 <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700">
            Failed to load earnings data. Please try again.
          </div>
        ) : (
          <>
 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
 <div className="rounded-xl border border-border bg-surface-muted/80 p-4 sm:p-5">
 <div className="flex items-start justify-between gap-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
 Platform earnings
 </p>
 <p className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-ink tabular-nums">
                  ${earnings.total_earnings?.toFixed(2) || '0.00'}
                </p>
 <p className="mt-1.5 text-xs text-ink-tertiary">
 {earnings.start_date} – {earnings.end_date}
 </p>
 <p className="mt-1 text-xs text-ink-tertiary">
 {PRIVATE_PAYOUT_EXCLUDED_NOTE} for private bookings
                </p>
              </div>
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-gray-500">
 <DollarSign className="w-4 h-4" />
 </div>
 </div>
 </div>

 <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-4 sm:p-5">
 <div className="flex items-start justify-between gap-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
 Net platform payout
 </p>
 <p className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-emerald-700 tabular-nums">
 ${earnings.net_earnings?.toFixed(2) || '0.00'}
 </p>
 <p className="mt-1.5 text-xs text-ink-tertiary">After platform fees</p>
 </div>
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-surface text-emerald-600">
 <Wallet className="w-4 h-4" />
 </div>
 </div>
                </div>

 <div className="rounded-xl border border-border bg-surface-muted/80 p-4 sm:p-5">
 <div className="flex items-start justify-between gap-3">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
 Private offline collected
 </p>
 <p className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-ink tabular-nums">
 ${earnings.private_offline_earnings?.toFixed(2) || '0.00'}
 </p>
 <p className="mt-1.5 text-xs text-ink-tertiary">
 {PRIVATE_PAYOUT_EXCLUDED_NOTE}
 </p>
 </div>
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink-secondary">
 <Lock className="w-4 h-4" />
 </div>
 </div>
 </div>
              </div>

 <div className="rounded-xl border border-border bg-surface-muted/40 p-4 sm:p-5">
 <div className="flex items-start gap-3 mb-4">
 <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink-secondary">
 <TrendingUp className="w-4 h-4" />
                </div>
 <div>
 <h2 className="text-sm font-semibold text-ink">
 Earnings breakdown
 </h2>
 <p className="text-xs text-ink-tertiary mt-0.5">
 Platform bookings only
 </p>
              </div>
            </div>

              {earnings.earnings_by_service && earnings.earnings_by_service.length > 0 ? (
 <div className="space-y-2.5">
                  {earnings.earnings_by_service.map((service, index) => (
 <div
 key={index}
 className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-border bg-surface px-4 py-3.5"
 >
 <div className="min-w-0">
 <p className="text-sm font-semibold text-ink truncate">
 {service.service_name || 'Unknown service'}
 </p>
 <p className="text-xs text-ink-tertiary mt-0.5">
 {service.bookings_count || 0} bookings
 </p>
                      </div>
 <p className="text-sm font-semibold text-ink tabular-nums shrink-0">
                        ${service.total_earnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
 <div className="py-10 text-center">
 <p className="text-sm font-medium text-ink-secondary">
 No earnings data for this period
 </p>
 <p className="mt-1 text-xs text-ink-tertiary">
 Completed platform bookings will appear here.
 </p>
 </div>
              )}
            </div>
          </>
        )}
 </div>
 </div>
 </div>
  );
};

export default VendorEarnings;
